import * as React from 'react';

import { translate } from '~l10n/client';

import { PERSONAL_LIBRARY_SORT_SESSION_KEY } from '~teacher/components/quizzes/QuizList/librarySortSessionKeys';
import { Divider } from '~shared/components/Divider';
import { Direction } from '~shared/utils/sort';
import { useColumnSortReducer } from '~teacher/components/quizzes';
import { LibraryPanel } from '~teacher/components/quizzes/LibraryPanel/LibaryPanel';
import { useFilteredSortedQuizListItems } from '~teacher/components/quizzes/QuizList/useFilteredSortedQuizListItems';
import { getSearchQueryFilter } from '~teacher/components/quizzes/QuizListFilters';
import { useQuizzes } from '~teacher/hook';
import { sortQuizListItems } from '~teacher/pages/sortQuizListItems';
import { proCheck } from '~teacher/util/proCheck';

import { QuizOrFolder } from '../../../../api/quiz/getQuizzes/types';
import { personalQuizUrl as quizUrl } from '../../../../api/urls';

interface Props {
  searchQuery: string;
  setParentId: (newParentId: number) => void;
  closeSearchField: () => void;
}

export function LibraryPanelBySearch({
  searchQuery,
  setParentId,
  closeSearchField,
}: Props) {
  const quizzes = useQuizzes();

  const [
    { direction: sortDirection, quizListColumn: sortProperty },
    dispatchColumnSortAction,
  ] = useColumnSortReducer({
    initialToggleDirection: Direction.Ascending,
    sessionStorageKey: PERSONAL_LIBRARY_SORT_SESSION_KEY,
  });

  const sortQuizItems = React.useCallback(
    (items: QuizOrFolder[]): QuizOrFolder[] =>
      sortQuizListItems({
        items: items,
        sortProperty: sortProperty,
        sortDirection: sortDirection,
      }),
    [sortProperty, sortDirection]
  );
  const {
    selectedQuizItem,
    isSelectedAll,
    toggleSelectAll,
    toggleItem,
    quizOrFolders: sortedQuizItems,
    selectNone,
  } = useFilteredSortedQuizListItems({
    quizListItems: quizzes.data,
    filter: getSearchQueryFilter({
      searchQuery: searchQuery,
      canMakeFolders: proCheck.canMakeFolders(),
    }),
    sort: sortQuizItems,
  });

  // This is a workaround to ensure translation and UI is in sync for languages where subject is first (e.g. Korean)
  const translationTemplateString = translate('Nothing found matching “{0}”');
  // Pull out text before/after the {0}, stripping any surrounding quotes. Helps with languages that uses „…“ (U+201E / U+201C) quotes (e.g. German)
  const matchResult = translationTemplateString.match(/^(?<before>[\s\S]*?)\s*(?:["“”„«»‹›‚'’「『]?\s*)\{0\}(?:\s*["“”„«»‹›‚'’」』]?)\s*(?<after>[\s\S]*)$/u);
  const before = matchResult?.groups?.before ?? translationTemplateString;  // fallback: whole string if no match
  const after  = matchResult?.groups?.after  ?? '';


  if (sortedQuizItems.length === 0 && searchQuery) {
    return (
      <div className="pt-10">
        <Divider />
        <div className="p-10 flex flex-col items-center justify-start">
          {/* tooltip shows the full query if it is longer than 60 characters */}
          <h3 className="m-0 font-display text-blue-900 text-md font-400 leading-16 text-center">
            {before}
            <br />
            <q dir="auto">
              <span
                className="inline-block truncate align-bottom"
                style={{ maxWidth: '60ch' }}
                title={searchQuery}>
                {searchQuery}
              </span>
            </q>
            <br />
            {after}
          </h3>
          <p className="text-sm font-300 mt-2">
            {translate('Delete search terms to start over.')}
          </p>
        </div>
        <Divider />
      </div>
    );
  }

  return (
    <LibraryPanel
      QuizUrl={quizUrl}
      setParentId={setParentId}
      sortProperty={sortProperty}
      sortDirection={sortDirection}
      isSelectedAll={isSelectedAll}
      selectedQuizItem={selectedQuizItem}
      dispatchColumnSortAction={dispatchColumnSortAction}
      selectNone={selectNone}
      toggleSelectAll={toggleSelectAll}
      toggleItem={toggleItem}
      quizItems={sortedQuizItems}
      closeSearchField={closeSearchField}
    />
  );
}
